/**
 * WorkoutPreferencesMatrix - Grid-based workout preference selector
 *
 * A matrix UI where:
 * - Columns = Days of the week (Mon-Sun)
 * - Rows = Workout types (Rest, Base Run, Hard Run, Long Run, Base Ride, etc.)
 * - Single click to assign a workout type to a day
 */

import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import type {
  DayOfWeek,
  UserProfile,
  WeeklyWorkoutPreferences,
  WorkoutPreferenceType,
} from '@shared/types';
import React from 'react';

interface WorkoutPreferencesMatrixProps {
  profile: UserProfile;
  onChange: (updatedProfile: Partial<UserProfile>) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const DAYS_OF_WEEK: { full: DayOfWeek; short: string }[] = [
  { full: 'Monday', short: 'Mon' },
  { full: 'Tuesday', short: 'Tue' },
  { full: 'Wednesday', short: 'Wed' },
  { full: 'Thursday', short: 'Thu' },
  { full: 'Friday', short: 'Fri' },
  { full: 'Saturday', short: 'Sat' },
  { full: 'Sunday', short: 'Sun' },
];

interface WorkoutTypeConfig {
  value: WorkoutPreferenceType;
  label: string;
  shortLabel: string;
  icon: string;
  color: string;
  sport: 'rest' | 'run' | 'ride';
}

const WORKOUT_TYPES: WorkoutTypeConfig[] = [
  { value: 'rest', label: 'Rest', shortLabel: 'Rest', icon: '🛌', color: '#616161', sport: 'rest' },
  {
    value: 'base run',
    label: 'Base Run',
    shortLabel: 'Base',
    icon: '🏃',
    color: '#388e3c',
    sport: 'run',
  },
  {
    value: 'hard run',
    label: 'Hard Run',
    shortLabel: 'Hard',
    icon: '⚡🏃',
    color: '#d32f2f',
    sport: 'run',
  },
  {
    value: 'long run',
    label: 'Long Run',
    shortLabel: 'Long',
    icon: '📏🏃',
    color: '#f57c00',
    sport: 'run',
  },
  {
    value: 'base ride',
    label: 'Base Ride',
    shortLabel: 'Base',
    icon: '🚴',
    color: '#0288d1',
    sport: 'ride',
  },
  {
    value: 'hard ride',
    label: 'Hard Ride',
    shortLabel: 'Hard',
    icon: '⚡🚴',
    color: '#c2185b',
    sport: 'ride',
  },
  {
    value: 'long ride',
    label: 'Long Ride',
    shortLabel: 'Long',
    icon: '📏🚴',
    color: '#7b1fa2',
    sport: 'ride',
  },
];

const WorkoutPreferencesMatrix: React.FC<WorkoutPreferencesMatrixProps> = ({
  profile,
  onChange,
  onValidationChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const preferences = profile.weeklyWorkoutPreferences || ({} as WeeklyWorkoutPreferences);

  // Get available workout types based on training ratio
  const runPercent = profile.trainingRatio?.running ?? 100;
  const bikePercent = 100 - runPercent;

  const getAvailableTypes = (): WorkoutPreferenceType[] => {
    const available: WorkoutPreferenceType[] = ['rest'];

    if (runPercent === 100) {
      return [...available, 'base run', 'hard run', 'long run'];
    }
    if (bikePercent === 100) {
      return [...available, 'base ride', 'hard ride', 'long ride'];
    }

    // Mixed - show all
    return WORKOUT_TYPES.map(t => t.value);
  };

  const availableTypes = getAvailableTypes();
  const visibleWorkoutTypes = WORKOUT_TYPES.filter(t => availableTypes.includes(t.value));

  const handleCellClick = (day: DayOfWeek, type: WorkoutPreferenceType) => {
    const getPrefs = (d: DayOfWeek) => preferences[d] || ['rest'];
    const updatedPreferences: WeeklyWorkoutPreferences = {
      Monday: getPrefs('Monday'),
      Tuesday: getPrefs('Tuesday'),
      Wednesday: getPrefs('Wednesday'),
      Thursday: getPrefs('Thursday'),
      Friday: getPrefs('Friday'),
      Saturday: getPrefs('Saturday'),
      Sunday: getPrefs('Sunday'),
      [day]: [type], // Store as single-item array
    };

    onChange({ weeklyWorkoutPreferences: updatedPreferences });
  };

  // Helper to get first preference from array
  const getFirstPref = (prefs: WorkoutPreferenceType[] | undefined): WorkoutPreferenceType => {
    if (Array.isArray(prefs)) {
      return prefs[0] || 'rest';
    }
    return 'rest';
  };

  // Validation: at least one training day
  const trainingDays = DAYS_OF_WEEK.filter(d => {
    const pref = getFirstPref(preferences[d.full]);
    return pref && pref !== 'rest';
  }).length;

  React.useEffect(() => {
    onValidationChange?.(trainingDays > 0);
  }, [trainingDays, onValidationChange]);

  // Summary stats
  const runDays = DAYS_OF_WEEK.filter(d => {
    const pref = getFirstPref(preferences[d.full]);
    return pref.includes('run');
  }).length;
  const rideDays = DAYS_OF_WEEK.filter(d => {
    const pref = getFirstPref(preferences[d.full]);
    return pref.includes('ride');
  }).length;
  const restDays = 7 - trainingDays;

  // Training Mix Validation (with tolerance)
  const TOLERANCE = 15; // ±15% tolerance for flexibility
  const activeDays = runDays + rideDays;
  const actualRunPercent = activeDays > 0 ? Math.round((runDays / activeDays) * 100) : 0;
  const actualBikePercent = activeDays > 0 ? Math.round((rideDays / activeDays) * 100) : 0;

  // Check if schedule matches training mix slider (within tolerance)
  const runMismatch = activeDays > 0 && Math.abs(actualRunPercent - runPercent) > TOLERANCE;
  const bikeMismatch = activeDays > 0 && Math.abs(actualBikePercent - bikePercent) > TOLERANCE;
  const hasMixMismatch = runMismatch || bikeMismatch;

  // Preferred Long Day Validation
  const preferredLongDay = profile.preferredLongDay || 'Saturday';
  const longDayPref = getFirstPref(preferences[preferredLongDay]);
  const isLongWorkout = longDayPref === 'long run' || longDayPref === 'long ride';
  const hasLongDayMismatch = longDayPref && longDayPref !== 'rest' && !isLongWorkout;

  return (
    <Box>
      {/* Matrix Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${isMobile ? '70px' : '90px'} repeat(7, 1fr)`,
          gap: '2px',
          fontSize: isMobile ? '10px' : '11px',
        }}
      >
        {/* Header Row - Days */}
        <Box /> {/* Empty corner cell */}
        {DAYS_OF_WEEK.map(day => (
          <Box
            key={day.full}
            sx={{
              textAlign: 'center',
              py: 1,
              fontWeight: 600,
              color: 'primary.main',
              bgcolor: 'action.hover',
              borderRadius: '4px',
            }}
          >
            {isMobile ? day.short.slice(0, 2) : day.short}
          </Box>
        ))}
        {/* Workout Type Rows */}
        {visibleWorkoutTypes.map(workoutType => (
          <React.Fragment key={workoutType.value}>
            {/* Row Label */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                bgcolor: 'action.hover',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <span>{workoutType.icon}</span>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {isMobile ? workoutType.shortLabel : workoutType.label}
              </Typography>
            </Box>

            {/* Day Cells */}
            {DAYS_OF_WEEK.map(day => {
              const isSelected = getFirstPref(preferences[day.full]) === workoutType.value;

              return (
                <Box
                  key={`${day.full}-${workoutType.value}`}
                  onClick={() => handleCellClick(day.full, workoutType.value)}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={`Set ${day.full} to ${workoutType.label}`}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCellClick(day.full, workoutType.value);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: isMobile ? 0.5 : 0.75,
                    bgcolor: isSelected ? workoutType.color : 'action.hover',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: isSelected ? workoutType.color : 'action.selected',
                      transform: 'scale(1.05)',
                    },
                    '&:focus': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: '1px',
                    },
                  }}
                >
                  {/* Radio Dot */}
                  <Box
                    sx={{
                      width: isMobile ? 12 : 14,
                      height: isMobile ? 12 : 14,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: isSelected ? '#fff' : 'divider',
                      bgcolor: isSelected ? '#fff' : 'transparent',
                      transition: 'all 0.15s',
                    }}
                  />
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 2,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            🏃 Run
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {runDays} day{runDays !== 1 ? 's' : ''}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({actualRunPercent}%)
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            🚴 Ride
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {rideDays} day{rideDays !== 1 ? 's' : ''}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({actualBikePercent}%)
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            🛌 Rest
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {restDays} day{restDays !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Training Mix Mismatch Warning */}
      {hasMixMismatch && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'warning.light',
            borderRadius: 1,
            border: 1,
            borderColor: 'warning.main',
          }}
        >
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>
            ⚠️ <strong>Schedule doesn't match Training Mix</strong>
          </Typography>
          <Typography variant="caption" color="warning.dark">
            Your schedule shows {actualRunPercent}% running / {actualBikePercent}% cycling, but the
            slider is set to {runPercent}% / {bikePercent}%. Adjust your schedule or the slider to
            match (±{TOLERANCE}% tolerance).
          </Typography>
        </Box>
      )}

      {/* Preferred Long Day Mismatch Warning */}
      {hasLongDayMismatch && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'warning.light',
            borderRadius: 1,
            border: 1,
            borderColor: 'warning.main',
          }}
        >
          <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>
            ⚠️ <strong>Preferred Long Day mismatch</strong>
          </Typography>
          <Typography variant="caption" color="warning.dark">
            {preferredLongDay} is set as your preferred long workout day, but it's currently
            assigned "{longDayPref}". Set it to "Long Run" or "Long Ride" to match.
          </Typography>
        </Box>
      )}

      {/* Validation Warning */}
      {trainingDays === 0 && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          Please select at least one training day
        </Typography>
      )}
    </Box>
  );
};

export default WorkoutPreferencesMatrix;
